// Modal de Configuración — huso horario, idioma, notificaciones
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTexts, TXT } from '../../context/TextContext';
import { subscriptionsApi } from '../../services/api';
import CustomSelect from '../CustomSelect';

const TIMEZONES = [
  { label: 'UTC',                                     value: 'UTC' },
  { label: 'America/Argentina/Buenos_Aires (ART -3)', value: 'America/Argentina/Buenos_Aires' },
  { label: 'America/Montevideo (UYT -3)',              value: 'America/Montevideo' },
  { label: 'America/Santiago (CLT -4/-3)',             value: 'America/Santiago' },
  { label: 'America/Bogota (COT -5)',                  value: 'America/Bogota' },
  { label: 'America/Lima (PET -5)',                    value: 'America/Lima' },
  { label: 'America/Mexico_City (CST -6/-5)',          value: 'America/Mexico_City' },
  { label: 'America/Chicago (CST -6/-5)',              value: 'America/Chicago' },
  { label: 'America/New_York (EST -5/-4)',             value: 'America/New_York' },
  { label: 'America/Denver (MST -7/-6)',               value: 'America/Denver' },
  { label: 'America/Los_Angeles (PST -8/-7)',          value: 'America/Los_Angeles' },
  { label: 'America/Sao_Paulo (BRT -3/-2)',            value: 'America/Sao_Paulo' },
  { label: 'Europe/London (GMT 0/+1)',                 value: 'Europe/London' },
  { label: 'Europe/Madrid (CET +1/+2)',                value: 'Europe/Madrid' },
  { label: 'Europe/Paris (CET +1/+2)',                 value: 'Europe/Paris' },
  { label: 'Europe/Berlin (CET +1/+2)',                value: 'Europe/Berlin' },
  { label: 'Asia/Dubai (GST +4)',                      value: 'Asia/Dubai' },
  { label: 'Asia/Kolkata (IST +5:30)',                 value: 'Asia/Kolkata' },
  { label: 'Asia/Tokyo (JST +9)',                      value: 'Asia/Tokyo' },
  { label: 'Australia/Sydney (AEST +10/+11)',          value: 'Australia/Sydney' },
];

const OPT = [
  { value: 'A', labelEn: 'Automatic', labelEs: 'Automática' },
  { value: 'E', labelEn: 'Explicit',  labelEs: 'Explícita' },
  { value: 'N', labelEn: 'No',        labelEs: 'No' },
];

const HINTS = {
  A: { en: 'Every update will notify subscribers automatically', es: 'Cada actualización notificará a los suscriptores en forma automática' },
  E: { en: 'A button will be available to notify subscribers manually', es: 'Habrá un botón disponible para notificar a los suscriptores en forma manual' },
  N: { en: 'No notifications will be raised', es: 'No se generarán notificaciones' },
};

export default function ConfigModal({ show, onClose, showToast }) {
  const { user, updateTimezone, updateIdioma } = useAuth();
  const { t, idiId, loadTexts } = useTexts();
  const isEn = idiId === 1;

  const [tab, setTab]         = useState('prefs');
  const [saving, setSaving]   = useState(false);

  // Prefs
  const [timezone, setTimezone] = useState(user?.timezone || 'UTC');
  const [idioma,   setIdioma]   = useState(user?.idioma   || 'es');

  // Suscripciones
  const [docTypes,  setDocTypes]  = useState([]);
  const [subs,      setSubs]      = useState([]);
  const [notifLoad, setNotifLoad] = useState(false);
  const [selDocType, setSelDocType] = useState('');
  const [susData, setSusData] = useState('A');
  const [susFile, setSusFile] = useState('N');

  // Reset prefs when modal opens
  useEffect(() => {
    if (show) {
      setTimezone(user?.timezone || 'UTC');
      setIdioma(user?.idioma || 'es');
      setTab('prefs');
    }
  }, [show]);

  // Load suscripciones when tab opens
  useEffect(() => {
    if (show && tab === 'notif' && docTypes.length === 0) {
      setNotifLoad(true);
      Promise.all([subscriptionsApi.docTypes(), subscriptionsApi.list()])
        .then(([tdoRes, subsRes]) => {
          setDocTypes(tdoRes.data);
          setSubs(subsRes.data);
          if (tdoRes.data.length) setSelDocType(String(tdoRes.data[0].id));
        })
        .catch(() => showToast(t(TXT.ERROR_LOADING_SUBS, 'Error al cargar suscripciones'), 'error'))
        .finally(() => setNotifLoad(false));
    }
  }, [show, tab]);

  if (!show) return null;

  const nowPreview = (() => {
    try {
      return new Intl.DateTimeFormat(isEn ? 'en-US' : 'es-AR', {
        timeZone: timezone,
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(new Date());
    } catch { return ''; }
  })();

  const handleSavePrefs = async () => {
    setSaving(true);
    try {
      const promises = [];
      if (timezone !== user?.timezone) promises.push(updateTimezone(timezone));
      if (idioma   !== user?.idioma)   promises.push(updateIdioma(idioma).then(() => loadTexts()));
      await Promise.all(promises);
      showToast(t(TXT.DATA_SAVED, 'Guardado'));
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSub = async () => {
    if (!selDocType) return;
    if (susData === 'N' && susFile === 'N') { showToast(t(TXT.SELECT_ONE_OPTION, 'Seleccione al menos una opción'), 'warning'); return; }
    try {
      await subscriptionsApi.save(selDocType, { susData, susFile });
      const res = await subscriptionsApi.list();
      setSubs(res.data);
      setSusData('A');
      setSusFile('N');
      showToast(t(TXT.DATA_SAVED, 'Guardado'), 'success');
    } catch {
      showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const handleRemoveSub = async (tdoId) => {
    try {
      await subscriptionsApi.remove(tdoId);
      setSubs(prev => prev.filter(r => r.Tdo_IdTDoc !== tdoId));
    } catch {
      showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const optLabel = (val) => {
    const o = OPT.find(x => x.value === val);
    return o ? (isEn ? o.labelEn : o.labelEs) : val;
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
        <div className="modal-content" style={{ borderRadius: 12, overflow: 'hidden' }}>

          {/* Header + tabs */}
          <div className="modal-header flex-column align-items-start gap-0 pb-0" style={{ borderBottom: '1px solid #dee2e6' }}>
            <div className="d-flex align-items-center justify-content-between w-100 mb-3">
              <h5 className="modal-title d-flex align-items-center gap-2 mb-0">
                <i className="fa-solid fa-gear color1" style={{ fontSize: '0.95rem' }} />
                {t(TXT.SETTINGS, 'Configuración')}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="d-flex" style={{ marginBottom: -1 }}>
              {[
                { id: 'prefs', label: t(TXT.PREFERENCES, 'Preferencias') },
                ...(user?.functions?.includes(32) ? [{ id: 'notif', label: t(TXT.SUBSCRIPTIONS, 'Suscripciones') }] : []),
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  style={{
                    background: 'none', border: 'none', padding: '8px 16px',
                    fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                    color: tab === id ? 'var(--color1, #2a9d8f)' : '#6c757d',
                    borderBottom: tab === id ? '2px solid var(--color1, #2a9d8f)' : '2px solid transparent',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto', padding: '20px 24px' }}>

            {/* ── Tab: Preferencias ── */}
            {tab === 'prefs' && (
              <div className="d-flex flex-column gap-3">
                <div className="row g-3">
                  <div className="col-8">
                    <label className="form-label" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d' }}>
                      {t(TXT.TIME_ZONE, 'Huso horario')}
                    </label>
                    <CustomSelect value={timezone} onChange={e => setTimezone(e.target.value)}>
                      {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                    </CustomSelect>
                  </div>
                  <div className="col-4">
                    <label className="form-label" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d' }}>
                      {t(TXT.LANGUAGE, 'Idioma')}
                    </label>
                    <CustomSelect value={idioma} onChange={e => setIdioma(e.target.value)}>
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </CustomSelect>
                  </div>
                </div>
                {nowPreview && (
                  <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                    {t(TXT.CURRENT_TIME_IN_ZONE, 'Hora actual en zona seleccionada:')} <strong>{nowPreview}</strong>
                  </p>
                )}
              </div>
            )}

            {/* ── Tab: Suscripciones ── */}
            {tab === 'notif' && (
              <div className="d-flex flex-column gap-3">
                {notifLoad ? (
                  <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-secondary" /></div>
                ) : (
                  <>
                    {/* Formulario nueva suscripción */}
                    <div className="p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                      <div className="fw-500 mb-2" style={{ fontSize: '0.8rem', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t(TXT.NEW_SUBSCRIPTION, 'Nueva suscripción')}
                      </div>

                      <div className="mb-2">
                        <CustomSelect value={selDocType} onChange={e => setSelDocType(e.target.value)}>
                          {docTypes.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </CustomSelect>
                      </div>

                      {[
                        { label: t(TXT.DATA_UPDATED_SUB, 'Datos actualizados'), val: susData, set: setSusData, name: 'susData' },
                        { label: t(TXT.FILES_UPDATED, 'Archivos actualizados'), val: susFile, set: setSusFile, name: 'susFile' },
                      ].map(({ label, val, set, name }) => (
                        <div key={name} className="mb-2">
                          <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.8125rem', color: '#495057', minWidth: 150 }}>{label}</span>
                            <div className="d-flex gap-1">
                              {OPT.map(o => (
                                <label key={o.value} style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  padding: '3px 10px', borderRadius: 20,
                                  border: `1px solid ${val === o.value ? 'var(--color1, #2a9d8f)' : '#ced4da'}`,
                                  background: val === o.value ? 'rgba(42,157,143,0.1)' : '#fff',
                                  color: val === o.value ? 'var(--color1, #2a9d8f)' : '#6c757d',
                                  fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
                                }}>
                                  <input type="radio" name={name} value={o.value} checked={val === o.value}
                                    onChange={() => set(o.value)} style={{ display: 'none' }} />
                                  {isEn ? o.labelEn : o.labelEs}
                                </label>
                              ))}
                            </div>
                          </div>
                          {val && (
                            <div style={{ marginLeft: 158, marginTop: 3, fontSize: '0.72rem', color: '#6c757d', fontStyle: 'italic', lineHeight: 1.3 }}>
                              {isEn ? HINTS[val].en : HINTS[val].es}
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="d-flex justify-content-end">
                        <button className="button color1" style={{ fontSize: '0.8rem', padding: '4px 14px' }} onClick={handleAddSub}>
                          <i className="fa-solid fa-plus fa-xs color1 me-1" />
                          {t(TXT.SUBSCRIPTIONS, 'Suscribirse')}
                        </button>
                      </div>
                    </div>

                    {/* Lista de suscripciones */}
                    {subs.length === 0 ? (
                      <p className="text-muted fst-italic mb-0" style={{ fontSize: '0.8rem' }}>
                        {t(TXT.NO_SUBSCRIPTIONS, 'No hay suscripciones configuradas.')}
                      </p>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {subs.map(r => (
                          <div key={r.Tdo_IdTDoc} className="rounded overflow-hidden" style={{ border: '1px solid #dee2e6' }}>
                            <div className="d-flex align-items-center px-3 py-2" style={{ background: '#f8f9fa', fontSize: '0.8rem', fontWeight: 500, color: '#495057', borderBottom: '1px solid #dee2e6' }}>
                              <i className="fa-solid fa-file-lines me-2 color1 fa-xs" />
                              <span style={{ flex: 1 }}>{r.Tdo_NombTDoc}</span>
                              <i className="fa-solid fa-xmark text-danger fa-xs" style={{ cursor: 'pointer' }}
                                onClick={() => handleRemoveSub(r.Tdo_IdTDoc)} />
                            </div>
                            <div className="d-flex gap-3 px-3 py-2" style={{ fontSize: '0.8125rem' }}>
                              <span>
                                <span className="text-muted me-1">{t(TXT.DATA_LABEL, 'Datos:')}</span>
                                {optLabel(r.Sus_Data)}
                              </span>
                              <span>
                                <span className="text-muted me-1">{t(TXT.FILES_LABEL, 'Archivos:')}</span>
                                {optLabel(r.Sus_File)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="button" onClick={onClose}>
              {t(TXT.CANCEL, 'Cancelar')}
            </button>
            {tab === 'prefs' && (
              <button type="button" className="button color1" onClick={handleSavePrefs} disabled={saving}>
                {saving ? '…' : t(TXT.SAVE, 'Guardar')}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
